import type { IReactionDisposer } from 'mobx'
import { action, comparer, computed, makeObservable, observable, reaction } from 'mobx'

import type { Iter, ObjKey } from '@/types'
import { getValueFromKeyIter } from '@/utils/common'

import ExtendedMap from './ExtendedMap'

export class CheckableMap<K, V> extends ExtendedMap<K, V> {
  protected __dispose?: IReactionDisposer
  protected checkMap = new Map<K, V>()

  constructor(iterableIterator: IterableIterator<[K, V]> | Map<K, V>) {
    super(iterableIterator)

    makeObservable<CheckableMap<K, V>, 'checkMap'>(this, {
      checkMap: observable.shallow,
      isFullyChecked: computed,
      isNoChecked: computed,
      isPartChecked: computed,
      checkSize: computed,
      toCheckedValues: computed,
      toCheckedKeys: computed,
      setCheck: action,
      deleteCheck: action,
      toggleCheck: action,
      allCheck: action,
      clearAllCheck: action,
    })

    reaction(
      () => this.source,
      () => {
        const newCheckMap = new Map<K, V>()
        for (const checkedKey of this.checkMap.keys()) {
          const value = this.source.get(checkedKey)
          if (value) {
            newCheckMap.set(checkedKey, value)
          }
        }
        this.checkMap = newCheckMap
      },
      { equals: comparer.structural },
    )
  }

  get isFullyChecked() {
    return this.checkMap.size > 0 && this.source.size === this.checkSize
  }

  get isNoChecked() {
    return this.checkMap.size === 0
  }

  get isPartChecked() {
    return !this.isFullyChecked && !this.isNoChecked
  }

  get checkSize() {
    return this.checkMap.size
  }

  get toCheckedValues() {
    return [...this.checkMap.values()]
  }

  get toCheckedKeys() {
    return [...this.checkMap.keys()]
  }

  override get [Symbol.toStringTag]() {
    return 'CheckSet Map'
  }

  static override fromArr<keyT extends keyof valT, valT extends { [key in keyT]: valT[keyT] }>(
    coll: valT[],
    key: Iter<keyT, valT>,
  ) {
    const map = new this(new Map(coll.map((value) => [getValueFromKeyIter(value, key), value])))
    return map
  }

  static override fromObj<objT extends { [key in ObjKey]: valT }, valT>(obj: objT) {
    const map = new Map<keyof objT, objT[keyof objT]>()
    for (const key in obj) {
      map.set(key, obj[key])
    }
    return new this(map)
  }

  static override fromIter<K extends ObjKey, V>(iterableIterator: IterableIterator<[K, V]>) {
    return new this(iterableIterator)
  }

  override set(key: K, value: V) {
    this.source.set(key, value)
    if (this.checkMap.has(key)) {
      this.checkMap.set(key, value)
    }
    return this
  }

  dispose() {
    this.__dispose?.()
  }

  override delete(key: K) {
    this.source.delete(key)
    if (this.isChecked(key)) this.deleteCheck(key)
    return this
  }

  override deletes(keys: K[]) {
    keys.forEach((key) => this.delete(key))
    return this
  }

  override clear() {
    this.source.clear()
    this.checkMap.clear()
    return this
  }

  setCheck = (key: K) => {
    const value = this.source.get(key) /*?*/
    if (value) {
      this.checkMap.set(key, value)
      return this
    } else {
      throw Error(`체크할 key가 없습니다. key: ${key}`)
    }
  }

  deleteCheck = (key: K) => {
    this.checkMap.delete(key)
    return this
  }

  toggleCheck = (key: K) => {
    this.isChecked(key) ? this.deleteCheck(key) : this.setCheck(key)
    return this
  }

  isChecked = (key: K) => {
    return this.checkMap.has(key)
  }

  allCheck = () => {
    this.checkMap = new Map(this.source)
    return this
  }

  clearAllCheck = () => {
    this.checkMap.clear()
    return this
  }

  toggleAllCheck = () => {
    this.isFullyChecked ? this.clearAllCheck() : this.allCheck()
    return this
  }

  isAllCheckedByKeys = (keys: K[]) => {
    return keys.every((key) => this.isChecked(key))
  }
}

export default CheckableMap

// // 그냥 테스트중
// const mockData = [
//   { a: 10, b: 's' },
//   { a: 30, b: 'ss' },
// ]
// const mockObj = {
//   I2413: { a: 10, b: 30 },
//   I2443: { a: 10, b: 30 },
// }
// const checkableMap = CheckableMap.fromArr(mockData, 'a') /*?*/
// const checkableMap2 = CheckableMap.fromObj(mockObj) /*?*/
// checkableMap.get(10)!.a = 35 /*?*/
// checkableMap.get(30)!.a = 22 /*?*/
// type t = keyof typeof mockData
// // new CheckMap([1, 2, 3, 4].entries()).entries() /*?*/
//
// checkableMap.toKeyArr() /*?*/
// checkableMap.toCheckedValues /*?*/
// checkableMap.toCheckedKeys /*?*/
// checkableMap.setCheck(10) /*?*/
// checkableMap.toCheckedValues /*?*/
// checkableMap.toCheckedKeys /*?*/
//
// checkableMap.fromArr([{ a: 32, b: 'dd' }]) /*?*/
//
// checkableMap.toValueArr() /*?*/

// const d = CheckableMap.fromObj(mockObj)
